import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  BookOpen, Users, AlertTriangle, BarChart3, LogOut, 
  TrendingUp, MessageCircle, FileText, Trophy, Eye, Bell, Shield, QrCode, Settings, HelpCircle,
  Coins, Zap
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import axios from "axios";
import AdminTutorial from "@/components/AdminTutorial";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DirectorDashboard = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [allStudents, setAllStudents] = useState([]);
  const [usageData, setUsageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showTutorial, setShowTutorial] = useState(false);

  useEffect(() => {
    fetchData();
    // Show tutorial on first visit
    const tutorialCompleted = localStorage.getItem("sophia_admin_tutorial_completed");
    if (!tutorialCompleted) {
      setShowTutorial(true);
    }
  }, []);

  const fetchData = async () => {
    try {
      const [dashboardRes, alertsRes, studentsRes] = await Promise.all([
        axios.get(`${API}/dashboard/director`),
        axios.get(`${API}/alerts`),
        axios.get(`${API}/students`)
      ]);
      setDashboardData(dashboardRes.data);
      setAlerts(alertsRes.data);
      setAllStudents(studentsRes.data);
      
      // Fetch usage data if establishment ID is available
      if (dashboardRes.data?.etablissement_id) {
        try {
          const usageRes = await axios.get(`${API}/etablissements/${dashboardRes.data.etablissement_id}/usage`);
          setUsageData(usageRes.data);
        } catch (e) {
          console.log("Usage data not available");
        }
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const COLORS = ['#1A365D', '#D4AF37', '#22C55E'];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA]">
        <div className="text-center">
          <BookOpen className="w-12 h-12 text-[#1A365D] animate-pulse mx-auto" />
          <p className="mt-4 text-[#64748B]">Chargement du tableau de bord...</p>
        </div>
      </div>
    );
  }

  const classChartData = dashboardData?.classes?.map(c => ({
    name: c.name.replace("Terminale ", "T"),
    actifs: c.active_students,
    total: c.total_students,
    moyenne: c.average_points
  })) || [];

  const pieData = dashboardData?.classes?.map(c => ({
    name: c.name,
    value: c.total_students
  })) || [];

  return (
    <div className="min-h-screen bg-[#F8F9FA] overflow-x-hidden" data-testid="director-dashboard">
      {/* Navigation - Responsive */}
      <nav className="navbar px-3 md:px-8 py-3 md:py-4 flex items-center justify-between">
        <div className="flex items-center gap-2 md:gap-3">
          <div className="w-8 h-8 md:w-10 md:h-10 rounded-lg bg-[#1A365D] flex items-center justify-center">
            <BookOpen className="w-4 h-4 md:w-5 md:h-5 text-white" />
          </div>
          <span className="navbar-logo hidden sm:block">SophIA</span>
          <Badge className="ml-1 md:ml-2 bg-[#D4AF37] text-[#0F172A] text-xs">Direction</Badge>
        </div>
        
        {/* Desktop Navigation */}
        <div className="hidden lg:flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate("/observation")}
            className="border-[#22C55E] text-[#22C55E] hover:bg-[#22C55E] hover:text-white"
            data-testid="observation-button"
          >
            <Eye className="w-4 h-4 mr-1" />
            Suivi Chats
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate("/admin/schools")}
            className="border-[#1A365D] text-[#1A365D] hover:bg-[#1A365D] hover:text-white"
            data-testid="multi-school-button"
          >
            <Users className="w-4 h-4 mr-1" />
            Multi-Écoles
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate("/settings")}
            className="border-[#64748B] text-[#64748B] hover:bg-[#64748B] hover:text-white"
            data-testid="settings-button"
          >
            <Settings className="w-4 h-4 mr-1" />
            Paramètres
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate("/classes")}
            className="border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0F172A]"
            data-testid="classes-button"
          >
            <QrCode className="w-4 h-4 mr-1" />
            Classes & QR
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate("/moderation")}
            className="border-[#EF4444] text-[#EF4444] hover:bg-[#EF4444] hover:text-white"
            data-testid="moderation-button"
          >
            <Shield className="w-4 h-4 mr-1" />
            Modération
            {dashboardData?.unreviewed_incidents > 0 && (
              <Badge className="ml-1 bg-[#EF4444]">{dashboardData.unreviewed_incidents}</Badge>
            )}
          </Button>
        </div>

        {/* Mobile Navigation - Compact icons */}
        <div className="flex lg:hidden items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/observation")}
            className="p-2 text-[#22C55E]"
          >
            <Eye className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/admin/schools")}
            className="p-2 text-[#1A365D]"
          >
            <Users className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/settings")}
            className="p-2 text-[#64748B]"
          >
            <Settings className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/classes")}
            className="p-2 text-[#D4AF37]"
          >
            <QrCode className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/moderation")}
            className="p-2 text-[#EF4444] relative"
          >
            <Shield className="w-4 h-4" />
            {dashboardData?.unreviewed_incidents > 0 && (
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-[#EF4444] text-white text-xs rounded-full flex items-center justify-center">
                {dashboardData.unreviewed_incidents}
              </span>
            )}
          </Button>
        </div>

        {/* Common actions - All screens */}
        <div className="flex items-center gap-1 md:gap-2">
          <div className="relative">
            <Bell className="w-4 h-4 md:w-5 md:h-5 text-[#64748B] cursor-pointer hover:text-[#1A365D]" />
            {alerts.length > 0 && (
              <span className="notification-badge">{alerts.length}</span>
            )}
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => setShowTutorial(true)}
            className="text-[#64748B] hover:text-[#1A365D] p-2"
            data-testid="admin-help-button"
            title="Tutoriel"
          >
            <HelpCircle className="w-4 h-4" />
          </Button>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigate("/admin-aide")}
            className="text-[#64748B] hover:text-[#1A365D] p-2 hidden sm:flex"
            data-testid="admin-manual-button"
            title="Mode d'emploi"
          >
            <BookOpen className="w-4 h-4" />
          </Button>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={onLogout}
            className="text-[#64748B] hover:text-[#1A365D] p-2"
            data-testid="logout-button"
          >
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
      </nav>

      {/* Admin Tutorial */}
      {showTutorial && (
        <AdminTutorial 
          onComplete={() => setShowTutorial(false)}
          onSkip={() => setShowTutorial(false)}
        />
      )}

      <main className="px-4 md:px-8 py-6 max-w-7xl mx-auto page-enter">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl md:text-3xl text-[#1A365D] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
            Tableau de bord - Direction
          </h1>
          <p className="text-[#64748B]">Suivi des élèves de Terminale • Philosophie</p>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="card-sjh">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <Users className="w-5 h-5 text-[#1A365D]" />
                <span className="text-sm text-[#64748B]">Élèves inscrits</span>
              </div>
              <p className="stat-value">{dashboardData?.total_students || 0}</p>
            </CardContent>
          </Card>
          <Card className="card-sjh">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <FileText className="w-5 h-5 text-[#D4AF37]" />
                <span className="text-sm text-[#64748B]">Exercices complétés</span>
              </div>
              <p className="stat-value">{dashboardData?.total_exercises_completed || 0}</p>
            </CardContent>
          </Card>
          <Card className="card-sjh">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-5 h-5 text-[#22C55E]" />
                <span className="text-sm text-[#64748B]">Moyenne générale</span>
              </div>
              <p className="stat-value">{dashboardData?.average_score || 0}/20</p>
            </CardContent>
          </Card>
          <Card className="card-sjh">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <MessageCircle className="w-5 h-5 text-[#1A365D]" />
                <span className="text-sm text-[#64748B]">Messages IA</span>
              </div>
              <p className="stat-value">{dashboardData?.total_chat_messages || 0}</p>
            </CardContent>
          </Card>
        </div>

        {/* Usage Stats - Token & Cost Counter */}
        {usageData && (
          <Card className="mb-8 border-[#D4AF37]/30 bg-gradient-to-r from-[#FFFBEB] to-white" data-testid="usage-section">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg text-[#1A365D] flex items-center gap-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                <Coins className="w-5 h-5 text-[#D4AF37]" />
                Utilisation IA - {usageData.etablissement_name || "Établissement"}
              </CardTitle>
              <CardDescription>Suivi de la consommation en tokens et crédits</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-3 bg-white rounded-lg border border-[#E2E8F0]">
                  <div className="flex items-center justify-center gap-1 text-[#D4AF37] mb-1">
                    <Zap className="w-4 h-4" />
                    <span className="text-xs font-medium">Tokens (mois)</span>
                  </div>
                  <p className="text-xl font-bold text-[#1A365D]">
                    {(usageData.month_tokens || 0).toLocaleString()}
                  </p>
                </div>
                <div className="text-center p-3 bg-white rounded-lg border border-[#E2E8F0]">
                  <div className="flex items-center justify-center gap-1 text-[#22C55E] mb-1">
                    <Coins className="w-4 h-4" />
                    <span className="text-xs font-medium">Coût (mois)</span>
                  </div>
                  <p className="text-xl font-bold text-[#1A365D]">
                    {(usageData.month_cost || 0).toFixed(2)}€
                  </p>
                </div>
                <div className="text-center p-3 bg-white rounded-lg border border-[#E2E8F0]">
                  <div className="flex items-center justify-center gap-1 text-[#64748B] mb-1">
                    <Zap className="w-4 h-4" />
                    <span className="text-xs font-medium">Tokens (total)</span>
                  </div>
                  <p className="text-xl font-bold text-[#1A365D]">
                    {(usageData.total_tokens_used || 0).toLocaleString()}
                  </p>
                </div>
                <div className="text-center p-3 bg-white rounded-lg border border-[#E2E8F0]">
                  <div className="flex items-center justify-center gap-1 text-[#64748B] mb-1">
                    <Coins className="w-4 h-4" />
                    <span className="text-xs font-medium">Coût (total)</span>
                  </div>
                  <p className="text-xl font-bold text-[#1A365D]">
                    {(usageData.total_cost || 0).toFixed(2)}€
                  </p>
                </div>
              </div>
              <p className="text-xs text-[#64748B] mt-3 text-center">
                {usageData.total_operations || 0} opérations effectuées • Aujourd'hui: {(usageData.today_cost || 0).toFixed(4)}€
              </p>
            </CardContent>
          </Card>
        )}

        {/* Alerts Section */}
        {alerts.length > 0 && (
          <Card className="mb-8 border-[#EF4444]" data-testid="alerts-section">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg text-[#EF4444] flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                Alertes ({alerts.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-48">
                <div className="space-y-2">
                  {alerts.map((alert, index) => (
                    <div 
                      key={index} 
                      className={`alert-card ${alert.alert_type === 'difficulte' ? 'alert-card-danger' : ''}`}
                      data-testid={`alert-${index}`}
                    >
                      <div className="flex-1">
                        <p className="font-medium text-[#0F172A]">{alert.student_name}</p>
                        <p className="text-sm text-[#64748B]">{alert.class_name}</p>
                        <p className="text-sm mt-1">{alert.message}</p>
                      </div>
                      <Badge className={alert.alert_type === 'inactif' ? 'bg-[#EAB308]' : 'bg-[#EF4444]'}>
                        {alert.alert_type === 'inactif' ? 'Inactif' : 'En difficulté'}
                      </Badge>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        )}

        {/* Charts Row */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* Bar Chart - Activity by Class */}
          <Card className="card-sjh">
            <CardHeader>
              <CardTitle className="text-lg text-[#1A365D]" style={{ fontFamily: 'Playfair Display, serif' }}>
                Activité par classe
              </CardTitle>
              <CardDescription>Élèves actifs vs Total</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={classChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="name" stroke="#64748B" />
                  <YAxis stroke="#64748B" />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#FFFFFF', 
                      border: '1px solid #E2E8F0',
                      borderRadius: '8px'
                    }} 
                  />
                  <Bar dataKey="total" fill="#E2E8F0" name="Total" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="actifs" fill="#1A365D" name="Actifs" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Pie Chart - Distribution */}
          <Card className="card-sjh">
            <CardHeader>
              <CardTitle className="text-lg text-[#1A365D]" style={{ fontFamily: 'Playfair Display, serif' }}>
                Répartition des élèves
              </CardTitle>
              <CardDescription>Par classe de Terminale</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={90}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex justify-center gap-4 mt-4">
                {pieData.map((entry, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <div 
                      className="w-3 h-3 rounded-full" 
                      style={{ backgroundColor: COLORS[index] }}
                    />
                    <span className="text-sm text-[#64748B]">{entry.name}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Students Table */}
        <Card className="card-sjh" data-testid="students-table">
          <CardHeader>
            <CardTitle className="text-lg text-[#1A365D]" style={{ fontFamily: 'Playfair Display, serif' }}>
              Tous les élèves
            </CardTitle>
            <CardDescription>Suivi individuel</CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="all">
              <TabsList className="mb-4">
                <TabsTrigger value="all">Tous</TabsTrigger>
                <TabsTrigger value="Terminale A">Term. A</TabsTrigger>
                <TabsTrigger value="Terminale B">Term. B</TabsTrigger>
                <TabsTrigger value="Terminale C">Term. C</TabsTrigger>
              </TabsList>
              
              {["all", "Terminale A", "Terminale B", "Terminale C"].map(tabValue => (
                <TabsContent key={tabValue} value={tabValue}>
                  <ScrollArea className="h-96">
                    <table className="table-sjh">
                      <thead>
                        <tr>
                          <th>Nom</th>
                          <th>Classe</th>
                          <th>Points</th>
                          <th>Niveau</th>
                          <th>Diagnostic</th>
                          <th>Dernière activité</th>
                        </tr>
                      </thead>
                      <tbody>
                        {allStudents
                          .filter(s => tabValue === "all" || s.class_name === tabValue)
                          .map((student, index) => {
                            const lastActivity = new Date(student.last_activity);
                            const daysSince = Math.floor((Date.now() - lastActivity) / (1000 * 60 * 60 * 24));
                            const isInactive = daysSince > 3;
                            
                            return (
                              <tr 
                                key={index} 
                                data-testid={`student-row-${index}`}
                                className="cursor-pointer hover:bg-[#F8F9FA]"
                                onClick={() => navigate(`/tracking?id=${student.id}`)}
                              >
                                <td className="font-medium">
                                  <span className="flex items-center gap-2">
                                    {student.first_name} {student.last_name}
                                    {(isInactive || (student.diagnostic_completed && student.diagnostic_score < 40)) && (
                                      <AlertTriangle className="w-4 h-4 text-[#EF4444]" />
                                    )}
                                  </span>
                                </td>
                                <td>{student.class_name}</td>
                                <td>
                                  <span className="flex items-center gap-1">
                                    <Trophy className="w-4 h-4 text-[#D4AF37]" />
                                    {student.points}
                                  </span>
                                </td>
                                <td>
                                  <Badge className="bg-[#1A365D]">Niv. {student.level}</Badge>
                                </td>
                                <td>
                                  {student.diagnostic_completed ? (
                                    <Badge className={student.diagnostic_score >= 50 ? "bg-[#22C55E]" : "bg-[#EF4444]"}>
                                      {student.diagnostic_score}%
                                    </Badge>
                                  ) : (
                                    <Badge variant="outline">Non fait</Badge>
                                  )}
                                </td>
                                <td>
                                  <span className={isInactive ? "text-[#EF4444]" : "text-[#64748B]"}>
                                    {isInactive ? `${daysSince}j inactif` : lastActivity.toLocaleDateString('fr-FR')}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                        {allStudents.filter(s => tabValue === "all" || s.class_name === tabValue).length === 0 && (
                          <tr>
                            <td colSpan={6} className="text-center py-8 text-[#64748B]">
                              Aucun élève inscrit
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </ScrollArea>
                </TabsContent>
              ))}
            </Tabs>
          </CardContent>
        </Card>

        {/* Leaderboard Link */}
        <div className="mt-6 text-center">
          <Button 
            variant="outline" 
            onClick={() => navigate("/leaderboard")}
            className="border-[#1A365D] text-[#1A365D] hover:bg-[#1A365D] hover:text-white"
            data-testid="leaderboard-button"
          >
            <Trophy className="w-4 h-4 mr-2" />
            Voir le classement complet
          </Button>
        </div>
      </main>
    </div>
  );
};

export default DirectorDashboard;
